import Experience from "@folio-2026/experience"
import { useGuestbookStore } from "@folio-2026/section-guestbook"
import { supabase } from "@folio-2026/shared"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

type Step = "nickname" | "location" | "message" | "confirmation" | "sent"

export default function AddMessage() {
  const { isAddMemoryOpen } = useGuestbookStore()
  const [step, setStep] = useState<Step>("nickname")
  const [currentInput, setCurrentInput] = useState("")
  const [nickname, setNickname] = useState("")
  const [location, setLocation] = useState("")
  const [message, setMessage] = useState("")
  const [shouldRender, setShouldRender] = useState(isAddMemoryOpen)
  const [isVisible, setIsVisible] = useState(false)

  const fadeDurationMs = 1000

  useEffect(() => {
    if (isAddMemoryOpen) {
      setShouldRender(true)
      setIsVisible(false)

      // Ensure we paint once with opacity-0, then transition to opacity-100.
      // A single rAF can still land before the first paint of the mounted portal.
      let rafId1 = 0
      let rafId2 = 0
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          setIsVisible(true)
        })
      })

      return () => {
        cancelAnimationFrame(rafId1)
        cancelAnimationFrame(rafId2)
      }
    }

    if (!shouldRender) return

    setIsVisible(false)
    const timeoutId = setTimeout(() => {
      setShouldRender(false)
    }, fadeDurationMs)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isAddMemoryOpen, shouldRender])

  // Stop/resume lenis scroll when CLIInterface opens/closes
  useEffect(() => {
    const experience = Experience.getInstance()
    if (shouldRender) {
      experience.lenis?.stop()
    } else {
      experience.lenis?.start()
    }

    return () => {
      // Cleanup: resume scroll if component unmounts while open
      if (shouldRender) {
        experience.lenis?.start()
      }
    }
  }, [shouldRender])

  // Reset all fields when CLIInterface closes
  useEffect(() => {
    if (shouldRender) return

    setStep("nickname")
    setCurrentInput("")
    setNickname("")
    setLocation("")
    setMessage("")
  }, [shouldRender])

  if (!shouldRender) return null

  return createPortal(
    <div
      className={[
        "fixed inset-0 z-50 bg-white/10 backdrop-blur-sm p-4 flex lg:items-center lg:justify-center",
        "transition-opacity ease-out",
        isVisible ? "opacity-100" : "opacity-0",
        isVisible ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
      style={{ transitionDuration: `${fadeDurationMs}ms` }}
    >
      <div className="relative flex flex-col w-full h-full lg:w-[min(48rem,calc(100vw-2rem))] lg:h-[min(40rem,calc(100vh-2rem))] lg:rounded-2xl lg:border lg:border-white/10 lg:bg-black/40 lg:shadow-2xl overflow-hidden">
        <button
          className="absolute top-2 right-2 p-2 cursor-pointer"
          onClick={() => {
            useGuestbookStore.getState().setIsAddMemoryOpen(false)
          }}
        >
          x
        </button>
        <div className="flex flex-col flex-1 p-4">
          <CLIInterface
            step={step}
            setStep={setStep}
            currentInput={currentInput}
            setCurrentInput={setCurrentInput}
            nickname={nickname}
            setNickname={setNickname}
            location={location}
            setLocation={setLocation}
            message={message}
            setMessage={setMessage}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}

function CLIInterface({
  step,
  setStep,
  currentInput,
  setCurrentInput,
  nickname,
  setNickname,
  location,
  setLocation,
  message,
  setMessage,
}: {
  step: Step
  setStep: (step: Step) => void
  currentInput: string
  setCurrentInput: (input: string) => void
  nickname: string
  setNickname: (nickname: string) => void
  location: string
  setLocation: (location: string) => void
  message: string
  setMessage: (message: string) => void
}) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const justTransitionedToConfirmationRef = useRef(false)

  const addMemory = async (memoryData: {
    name: string
    origin: string
    message: string
  }) => {
    const client = await supabase()
    return await client
      .from("guestbook")
      .insert(memoryData)
      .then(() => {
        setStep("sent")
      })
  }

  // Auto-focus input when step changes to input steps or confirmation, blur when moving to sent
  useEffect(() => {
    // Handle focus/blur
    if (
      step === "nickname" ||
      step === "location" ||
      step === "message" ||
      step === "confirmation"
    ) {
      // Small delay to ensure the input is rendered
      const focusTimeoutId = setTimeout(() => {
        inputRef.current?.focus()
      }, 0)

      // Handle transition flag for confirmation step
      if (step === "confirmation") {
        // Set flag to prevent immediate submission
        justTransitionedToConfirmationRef.current = true
        // Clear the flag after a short delay to allow the next Enter press
        const flagTimeoutId = setTimeout(() => {
          justTransitionedToConfirmationRef.current = false
        }, 100)
        return () => {
          clearTimeout(focusTimeoutId)
          clearTimeout(flagTimeoutId)
        }
      }

      return () => clearTimeout(focusTimeoutId)
    } else {
      // Blur input when moving to sent step
      inputRef.current?.blur()
      justTransitionedToConfirmationRef.current = false
    }
  }, [step])

  // Handle any key press or click to close when sent
  useEffect(() => {
    if (step !== "sent") return

    const handleClose = () => {
      useGuestbookStore.getState().setIsAddMemoryOpen(false)
    }

    const handleKeyDown = () => {
      handleClose()
    }

    const handleClick = () => {
      handleClose()
    }

    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("click", handleClick)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("click", handleClick)
    }
  }, [step])

  // Keyboard input capture
  useEffect(() => {
    if (step === "sent") return

    const handleKeyDown = async (event: KeyboardEvent) => {
      // Skip character input and backspace handling if input is focused
      // (input's onChange and onKeyDown will handle these)
      const isInputFocused =
        document.activeElement === inputRef.current ||
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"

      // Handle Shift+Enter for line breaks in message field
      if (event.key === "Enter" && event.shiftKey && step === "message") {
        // Only handle if input is not focused (input will handle it naturally)
        if (!isInputFocused) {
          event.preventDefault()
          setCurrentInput(currentInput + "\n")
        }
        return
      }

      // Prevent default for Enter key
      if (event.key === "Enter") {
        event.preventDefault()
        event.stopPropagation()

        if (step === "nickname") {
          if (currentInput.length >= 2) {
            setNickname(currentInput)
            setCurrentInput("")
            setStep("location")
          }
        } else if (step === "location") {
          if (currentInput.length >= 2) {
            setLocation(currentInput)
            setCurrentInput("")
            setStep("message")
          }
        } else if (step === "message") {
          if (currentInput.length >= 2) {
            setMessage(currentInput)
            setCurrentInput("")
            setStep("confirmation")
          }
        } else if (step === "confirmation") {
          // Prevent immediate submission if we just transitioned to confirmation
          if (justTransitionedToConfirmationRef.current) {
            return
          }
          // Only process if we're actually on confirmation step and input is not focused
          // (input shouldn't be rendered on confirmation, but check to be safe)
          if (!isInputFocused) {
            await addMemory({
              name: nickname,
              origin: location,
              message,
            })
          }
        }
        return
      }

      // Handle Escape to close
      if (event.key === "Escape") {
        useGuestbookStore.getState().setIsAddMemoryOpen(false)
        return
      }

      // Only handle Backspace and character input if input is NOT focused
      // (when input is focused, it handles these naturally)
      if (!isInputFocused) {
        // Handle Backspace
        if (event.key === "Backspace") {
          if (step !== "confirmation") {
            setCurrentInput(currentInput.slice(0, -1))
          }
          return
        }

        // Handle character input (only for nickname, location, and message steps)
        if (step === "nickname" || step === "location" || step === "message") {
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
            setCurrentInput(currentInput + event.key)
          }
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [
    step,
    currentInput,
    nickname,
    location,
    message,
    setStep,
    setCurrentInput,
    setNickname,
    setLocation,
    setMessage,
  ])

  const renderPrompt = () => {
    if (step === "nickname") {
      return (
        <div className="font-mono text-white">
          <span>&gt; nickname: </span>
          <span>{currentInput}</span>
          <span className="animate-pulse">_</span>
        </div>
      )
    }

    if (step === "location") {
      return (
        <div className="font-mono text-white">
          <div>&gt; nickname: {nickname}</div>
          <div>
            <span>&gt; location: </span>
            <span>{currentInput}</span>
            <span className="animate-pulse">_</span>
          </div>
        </div>
      )
    }

    if (step === "message") {
      return (
        <div className="font-mono text-white">
          <div>&gt; nickname: {nickname}</div>
          <div>&gt; location: {location}</div>
          <div>
            <span>&gt; message: </span>
            <span className="whitespace-pre-wrap">{currentInput}</span>
            <span className="animate-pulse">_</span>
          </div>
        </div>
      )
    }

    if (step === "confirmation") {
      return (
        <div className="font-mono text-white">
          <div>&gt; nickname: {nickname}</div>
          <div>&gt; location: {location}</div>
          <div>
            <span>&gt; message: </span>
            <span className="whitespace-pre-wrap">{message}</span>
          </div>
          <div className="mt-4">&gt; Press enter again to send the memory.</div>
          <span className="animate-pulse">_</span>
        </div>
      )
    }

    if (step === "sent") {
      return (
        <div className="font-mono text-white">
          <div>&gt; nickname: {nickname}</div>
          <div>&gt; location: {location}</div>
          <div>
            <span>&gt; message: </span>
            <span className="whitespace-pre-wrap">{message}</span>
          </div>
          <div className="mt-4">Memory sent. Thank you.</div>
          <div className="mt-4">Press any key to continue.</div>
        </div>
      )
    }
  }

  // Handle input changes from the hidden input/textarea
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCurrentInput(e.target.value)
  }

  // Handle key events from the hidden input/textarea
  const handleInputKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Only handle events for input steps and confirmation
    if (
      step !== "nickname" &&
      step !== "location" &&
      step !== "message" &&
      step !== "confirmation"
    ) {
      return
    }

    // Handle Shift+Enter for line breaks in message field
    if (e.key === "Enter" && e.shiftKey && step === "message") {
      // Allow default behavior (newline)
      return
    }

    // Prevent default for Enter key
    if (e.key === "Enter") {
      e.preventDefault()

      if (step === "nickname") {
        if (currentInput.length >= 2) {
          setNickname(currentInput)
          setCurrentInput("")
          setStep("location")
        }
      } else if (step === "location") {
        if (currentInput.length >= 2) {
          setLocation(currentInput)
          setCurrentInput("")
          setStep("message")
        }
      } else if (step === "message") {
        if (currentInput.length >= 2) {
          setMessage(currentInput)
          setCurrentInput("")
          setStep("confirmation")
        }
      } else if (step === "confirmation") {
        // Prevent immediate submission if we just transitioned to confirmation
        if (justTransitionedToConfirmationRef.current) {
          return
        }
        await addMemory({
          name: nickname,
          origin: location,
          message,
        })
        setStep("sent")
      }
      return
    }

    // Handle Escape to close
    if (e.key === "Escape") {
      useGuestbookStore.getState().setIsAddMemoryOpen(false)
      return
    }

    // Backspace is handled naturally by the input element
  }

  const isInputStep =
    step === "nickname" || step === "location" || step === "message"
  const needsInputForKeyboard = isInputStep || step === "confirmation"

  return (
    <div className="flex flex-col flex-1">
      <KeyboardShortcuts step={step} />
      <div className="flex-1 relative mt-2">
        {renderPrompt()}
        {/* Hidden input/textarea for mobile keyboard support */}
        {needsInputForKeyboard &&
          (step === "message" ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={currentInput}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="absolute inset-0 opacity-0 w-full h-full resize-none"
              inputMode="text"
              autoComplete="off"
              aria-label="Message input"
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={step === "confirmation" ? "" : currentInput}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="absolute inset-0 opacity-0 w-full h-full"
              inputMode="text"
              autoComplete="off"
              aria-label={
                step === "nickname"
                  ? "Nickname input"
                  : step === "location"
                    ? "Location input"
                    : "Confirmation input"
              }
            />
          ))}
      </div>
    </div>
  )
}

function KeyboardShortcuts({ step }: { step: Step }) {
  return (
    <div className="-mx-2 lg:mx-0 pt-0 pb-2 border-b border-white/20 font-mono text-white/60 text-sm">
      <span className="flex items-center gap-1 lg:gap-4">
        <span>
          <kbd className="px-1 lg:px-2 py-1 bg-white/10 rounded text-white/90 font-semibold">
            ENTER
          </kbd>{" "}
          to submit
        </span>
        {step === "message" && (
          <span>
            <kbd className="px-2 py-1 bg-white/10 rounded text-white/90 font-semibold">
              SHIFT+ENTER
            </kbd>{" "}
            new line
          </span>
        )}
      </span>
    </div>
  )
}
